import { z } from 'zod';
import { AssetType, Jurisdiction, Criticality, ComplianceFramework } from './types';
import { SubscriptionPlan } from './types/subscription';

// Auth schemas
export const registerSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  name: z.string().min(2),
  companyName: z.string().optional(),
  companyCountry: z.string().optional(),
  companySector: z.string().optional(),
  selectedPlan: z.nativeEnum(SubscriptionPlan).optional(),
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});

// Organization schemas
export const createOrganizationSchema = z.object({
  name: z.string().min(2),
  sector: z.string().min(2),
  country: z.string().length(2), // ISO country code
  // Contact information (optional)
  contactEmail: z.string().email().optional().or(z.literal('').transform(() => undefined)),
  contactPhone: z.string().optional().or(z.literal('').transform(() => undefined)),
  address: z.string().optional().or(z.literal('').transform(() => undefined)),
  city: z.string().optional().or(z.literal('').transform(() => undefined)),
  postalCode: z.string().optional().or(z.literal('').transform(() => undefined)),
  websiteUrl: z.string().url().optional().or(z.literal('').transform(() => undefined)),
  // Legal information (optional)
  registrationNo: z.string().optional().or(z.literal('').transform(() => undefined)),
  vatNumber: z.string().optional().or(z.literal('').transform(() => undefined)),
  // DPO information (optional)
  dpoName: z.string().optional().or(z.literal('').transform(() => undefined)),
  dpoEmail: z.string().email().optional().or(z.literal('').transform(() => undefined)),
});

// Project schemas
export const createProjectSchema = z.object({
  organizationId: z.string().uuid(),
  name: z.string().min(2),
  description: z.string().optional(),
  framework: z.nativeEnum(ComplianceFramework).default(ComplianceFramework.GDPR),
});

// Asset schemas
export const createAssetSchema = z.object({
  projectId: z.string().uuid(),
  name: z.string().min(2),
  type: z.nativeEnum(AssetType),
  provider: z.string().min(2),
  jurisdiction: z.nativeEnum(Jurisdiction),
  criticality: z.nativeEnum(Criticality),
});

export const bulkAssetSchema = z.object({
  projectId: z.string().uuid(),
  assets: z.array(z.object({
    name: z.string().min(2),
    type: z.nativeEnum(AssetType),
    provider: z.string().min(2),
    jurisdiction: z.nativeEnum(Jurisdiction),
    criticality: z.nativeEnum(Criticality),
  })),
});

export const updateSubscriptionSchema = z.object({
  plan: z.nativeEnum(SubscriptionPlan),
});

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>;
export type CreateProjectInput = z.infer<typeof createProjectSchema>;
export type CreateAssetInput = z.infer<typeof createAssetSchema>;
export type BulkAssetInput = z.infer<typeof bulkAssetSchema>;
export type UpdateSubscriptionInput = z.infer<typeof updateSubscriptionSchema>;
